import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { ArrowLeft, MapPin, Loader2 } from "lucide-react";
import { monthLabelIST, formatISTDayLabel } from "../../lib/datetime";
import { plural } from "../../lib/vocabulary";
import { entryDoctorsLabel, PlannedDoctorChips } from "./plannedDoctors";
import PlanVsActual from "./PlanVsActual";

export default function TourPlanDetailPage() {
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/tour-plans/${id}`)
      .then(({ data }) => !cancelled && setPlan(data))
      .catch((e) => !cancelled && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className="flex items-center gap-2 text-callisto-muted text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  if (error || !plan) return (
    <div className="bg-white border border-callisto rounded-xl p-8 text-center">
      <h2 className="text-lg font-bold text-callisto-navy">Plan unavailable</h2>
      <p className="mt-2 text-sm text-callisto-muted">{error || "Not found"}</p>
      <Link to="/route-planner" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-callisto-gold hover:underline">
        <ArrowLeft size={14} /> Back
      </Link>
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="tour-plan-detail-page">
      <Link to="/route-planner" className="inline-flex items-center gap-1 text-sm text-callisto-muted hover:text-callisto-navy">
        <ArrowLeft size={14} /> Back
      </Link>
      <div className="bg-white border border-callisto rounded-xl p-5 md:p-7">
        <h1 className="text-2xl md:text-3xl font-bold text-callisto-navy">{plan.sales_officer_name}</h1>
        <p className="mt-1 text-sm text-callisto-muted">
          {monthLabelIST(plan.year, plan.month)} ·{" "}
          <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${
            plan.status === "Approved" ? "bg-emerald-50 text-emerald-700"
            : plan.status === "Submitted" ? "bg-amber-50 text-amber-700"
            : "bg-slate-100 text-slate-700"
          }`}>{plan.status}</span>
        </p>
        {plan.approver_remark && (
          <p className="mt-2 text-sm text-callisto-body italic">
            <span className="font-bold text-callisto-navy">{plan.approver_name}:</span> {plan.approver_remark}
          </p>
        )}
      </div>
      <div className="bg-white border border-callisto rounded-xl p-5 md:p-7">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3">
          Daily plan · {plural(plan.daily_plan?.length || 0, "day")}
        </p>
        {!plan.daily_plan?.length ? (
          <p className="text-sm text-callisto-muted">No days planned.</p>
        ) : (
          <ul className="divide-y divide-callisto" data-testid="tour-plan-detail-days">
            {[...plan.daily_plan].sort((a, b) => a.date.localeCompare(b.date)).map((e) => (
              <li key={e.date} className="py-3">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <p className="text-sm font-bold text-callisto-navy">{formatISTDayLabel(e.date)}</p>
                  <span className="text-[11px] text-callisto-muted">{entryDoctorsLabel(e)}</span>
                </div>
                {/* `area` is free-text locality, not a territory unit — see
                    MyTourPlanPage. Absent, it is named as absent rather than
                    leaving a pin with nothing after it. */}
                <p className="text-xs text-callisto-body mt-0.5 flex items-center gap-1">
                  <MapPin size={11} /> {e.area || <span className="text-callisto-muted italic">No area recorded</span>}
                </p>
                {/* The count above says how many; this says WHICH, with the
                    A/B/C that makes the route reviewable. `doctor_lookup` is
                    served once per plan and keyed by id — see
                    routes_tour_plans.doctor_lookup_for_plan. */}
                <PlannedDoctorChips entry={e} lookup={plan.doctor_lookup} />
                {e.remarks && <p className="text-[11px] text-callisto-muted mt-0.5 italic">"{e.remarks}"</p>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <PlanVsActual planId={id} />
    </div>
  );
}
